/**
 * EXP-01C — Event Story Page
 *
 * Immersive detail view for a single inspiration event.
 * Route: /explore/:slug
 * Public — no authentication required.
 *
 * CTA contract for this slice:
 *   - "Plan One Like This"  → disabled / Coming Next  (activates in EXP-01E)
 *   - "Build My Event"      → live → /build-my-event
 */

import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronDown, MapPin } from "lucide-react";
import { PJ } from "@/journey/journeyConfig";
import { loadJourneyFromStorage, saveJourneyToStorage, addJourneyPathEntry } from "@/journey/journeyEngine";
import { getJourneyContext, rankQAForContext } from "../utils/rankQA";
import { mapInspirationToJourneyFields } from "../utils/inspirationMapper";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Typography helpers ─────────────────────────────────────────────────────────
const serif = (extra = {}) => ({ fontFamily: "'Cormorant Garamond', Georgia, serif", ...extra });
const sans  = (extra = {}) => ({ fontFamily: "'DM Sans', sans-serif", ...extra });

// ── Field display maps ─────────────────────────────────────────────────────────
const GUEST_RANGE_LABELS = {
  "50_150":   "50 – 150 guests",
  "150_300":  "150 – 300 guests",
  "300_500":  "300 – 500 guests",
  "500_750":  "500 – 750 guests",
  "750_PLUS": "750+ guests",
};

const VENUE_SETTING_LABELS = {
  INDOOR:  "Indoor",
  OUTDOOR: "Outdoor",
  BOTH:    "Indoor & Outdoor",
};

const PLANNING_SCOPE_LABELS = {
  END_TO_END:   "End-to-End Planning",
  PARTIAL:      "Partial Planning",
  CONSULTATION: "Consultation",
};

const EVENT_TYPE_LABELS = {
  WEDDING:    "Wedding",
  SANGEET:    "Sangeet",
  ENGAGEMENT: "Engagement",
  BIRTHDAY:   "Birthday",
  CORPORATE:  "Corporate",
  CONFERENCE: "Conference",
  AWARDS:     "Awards",
};

// ── Global keyframes injected once ────────────────────────────────────────────
const storyStyles = `
  @keyframes storyFadeIn  { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
  @keyframes storySpin    { to { transform: rotate(360deg); } }
  .story-accordion-answer { animation: storyFadeIn 0.22s ease both; }
  .story-hero-img {
    width: 100%; height: 100%; object-fit: cover;
    transition: none;
  }
`;

// ── Sticky navigation ─────────────────────────────────────────────────────────
function StoryNav() {
  const nav = useNavigate();
  return (
    <nav
      data-testid="story-nav"
      style={{
        background: "rgba(32,27,25,0.96)",
        backdropFilter: "blur(12px)",
        position: "sticky", top: 0, zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 clamp(16px,4vw,56px)", height: 60,
        borderBottom: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      {/* Logo */}
      <button
        onClick={() => nav("/")}
        style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
      >
        <img
          src="/prashasta-logo.png"
          alt="Prashasta Events"
          data-testid="story-nav-logo"
          style={{ height: 28, width: "auto", objectFit: "contain" }}
          onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
        />
        <div style={{ display: "none", width: 28, height: 28, borderRadius: "50%", background: PJ.ruby, alignItems: "center", justifyContent: "center" }}>
          <span style={serif({ color: PJ.ivory, fontSize: 14, fontWeight: 600 })}>P</span>
        </div>
        <span style={serif({ color: PJ.ivory, fontSize: 18, fontWeight: 500, letterSpacing: "0.02em" })}>Prashasta</span>
      </button>

      {/* Links */}
      <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
        <button
          onClick={() => nav("/explore")}
          style={{
            background: "none", border: "none", cursor: "pointer",
            ...sans({ fontSize: 13, fontWeight: 500, color: PJ.ivory,
                      padding: "4px 0", borderBottom: `1.5px solid ${PJ.gold}` }),
          }}
          data-testid="story-nav-explore"
        >
          Explore
        </button>
        <button
          onClick={() => nav("/find-events-like-mine")}
          style={{ background: "none", border: "none", cursor: "pointer", ...sans({ fontSize: 13, color: PJ.slate }) }}
          data-testid="story-nav-find-style"
        >
          Find My Style
        </button>
        <button
          onClick={() => nav("/build-my-event")}
          data-testid="story-nav-build"
          style={{
            ...sans({ fontSize: 13, fontWeight: 600, color: PJ.white }),
            background: PJ.ruby, border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer",
          }}
        >
          Build My Event
        </button>
      </div>
    </nav>
  );
}

// ── Breadcrumb ─────────────────────────────────────────────────────────────────
function Breadcrumb() {
  const nav = useNavigate();
  return (
    <div style={{ background: PJ.obsidian, padding: "12px clamp(16px,4vw,56px)" }}>
      <button
        onClick={() => nav("/explore")}
        data-testid="story-breadcrumb-back"
        style={{
          background: "none", border: "none", cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 8,
          ...sans({ fontSize: 12, fontWeight: 500, color: PJ.slate, transition: "color 0.15s" }),
        }}
        onMouseEnter={e => { e.currentTarget.style.color = PJ.ivory; }}
        onMouseLeave={e => { e.currentTarget.style.color = PJ.slate; }}
      >
        <ArrowLeft size={14} />
        Explore Real Events
      </button>
    </div>
  );
}

// ── Hero ───────────────────────────────────────────────────────────────────────
function StoryHero({ event }) {
  // Inject per-event focal-point CSS so media queries can apply mobile values
  const fx  = event.hero_focal_x          ?? 50;
  const fy  = event.hero_focal_y          ?? 38;
  const mfx = event.hero_mobile_focal_x   ?? fx;
  const mfy = event.hero_mobile_focal_y   ?? fy;

  const focalCSS = `
    .story-hero-img {
      object-position: ${fx}% ${fy}%;
    }
    @media (max-width: 768px) {
      .story-hero-img {
        object-position: ${mfx}% ${mfy}%;
      }
    }
    .story-hero-wrap {
      height: min(68vh, 760px);
    }
    @media (max-width: 768px) {
      .story-hero-wrap {
        height: clamp(300px, 55vh, 480px);
      }
    }
  `;

  return (
    <>
      <style>{focalCSS}</style>
      <div
        data-testid="story-hero"
        className="story-hero-wrap"
        style={{
          position: "relative",
          overflow: "hidden",
          background: PJ.obsidian,
        }}
      >
        <img
          className="story-hero-img"
          src={event.hero_image_url}
          alt={event.title}
        />

        {/* Layered gradient — bottom-heavy for text legibility */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(32,27,25,0.93) 0%, rgba(32,27,25,0.42) 45%, rgba(32,27,25,0.12) 100%)",
          pointerEvents: "none",
        }} />

        {/* Content */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "36px clamp(16px,4vw,56px) 48px" }}>
          {/* Event type + style chips */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
            <span style={{
              ...sans({ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }),
              background: PJ.ruby, color: PJ.white, padding: "4px 10px", borderRadius: 4,
            }}>
              {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
            </span>
            {event.style_tags?.slice(0, 3).map(tag => (
              <span
                key={tag}
                style={{
                  ...sans({ fontSize: 10, fontWeight: 500 }),
                  background: "rgba(255,255,255,0.10)", backdropFilter: "blur(8px)",
                  color: PJ.sand, padding: "4px 10px", borderRadius: 4,
                  border: "1px solid rgba(255,255,255,0.14)",
                }}
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Title */}
          <h1
            data-testid="story-title"
            style={serif({
              fontSize: "clamp(2rem, 5vw, 4rem)",
              fontWeight: 400,
              color: PJ.ivory,
              lineHeight: 1.08,
              margin: "0 0 12px",
              maxWidth: 700,
            })}
          >
            {event.title}
          </h1>

          {/* Subtitle / location */}
          <p style={{ ...sans({ fontSize: "0.88rem", color: PJ.sand, margin: 0 }), display: "flex", alignItems: "center", gap: 6 }}>
            <MapPin size={14} style={{ color: PJ.gold, flexShrink: 0 }} />
            {event.subtitle || `${EVENT_TYPE_LABELS[event.event_type] || event.event_type} · ${event.location_city}`}
          </p>
        </div>
      </div>
    </>
  );
}

// ── Single detail item ─────────────────────────────────────────────────────────
function DetailItem({ label, value }) {
  return (
    <div>
      <p style={sans({ fontSize: "0.62rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: PJ.slate, margin: "0 0 5px" })}>
        {label}
      </p>
      <p style={sans({ fontSize: "0.95rem", fontWeight: 500, color: PJ.obsidian, margin: 0 })}>
        {value}
      </p>
    </div>
  );
}

// ── Horizontal chip list ───────────────────────────────────────────────────────
function ChipList({ items }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {items.map(item => (
        <span
          key={item}
          style={{
            ...sans({ fontSize: 12, fontWeight: 500, color: PJ.obsidian }),
            background: PJ.ivory, border: `1px solid ${PJ.sand}`,
            borderRadius: 20, padding: "5px 14px",
          }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

// ── Accordion Q&A item ─────────────────────────────────────────────────────────
function AccordionItem({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const id = q.slice(0, 24).replace(/[^a-z]/gi, "-").toLowerCase();

  return (
    <div style={{ borderBottom: `1px solid ${PJ.sand}` }}>
      <button
        data-testid={`qa-toggle-${id}`}
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          gap: 16, padding: "20px 0", textAlign: "left",
        }}
      >
        <span style={serif({ fontSize: "1.05rem", fontWeight: 500, color: PJ.obsidian, lineHeight: 1.3 })}>
          {q}
        </span>
        <span
          style={{
            color: PJ.gold, flexShrink: 0, marginTop: 3,
            transition: "transform 0.2s",
            display: "block",
            transform: open ? "rotate(180deg)" : "none",
          }}
        >
          <ChevronDown size={18} />
        </span>
      </button>

      {open && (
        <div
          className="story-accordion-answer"
          data-testid={`qa-answer-${id}`}
          style={{ paddingBottom: 20 }}
        >
          <p style={sans({ fontSize: "0.92rem", color: PJ.slate, lineHeight: 1.75, margin: 0 })}>
            {a}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Gallery strip (future multi-image support) ─────────────────────────────────
function GalleryStrip({ images, title }) {
  if (!images || images.length <= 1) return null;
  return (
    <section data-testid="story-gallery" style={{ background: "#F5F1EC", padding: "48px clamp(16px,4vw,56px)" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <p style={sans({ fontSize: "0.62rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: PJ.slate, margin: "0 0 20px" })}>
          Gallery
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {images.map((img, i) => (
            <div key={i} style={{ height: 220, borderRadius: 10, overflow: "hidden" }}>
              <img src={img} alt={`${title} · ${i + 1}`} loading="lazy"
                   style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CTA band ───────────────────────────────────────────────────────────────────
function StoryCTABand({ planningInspiration, handlePlanLikeThis }) {
  const nav = useNavigate();
  return (
    <section
      data-testid="story-cta-band"
      style={{ background: PJ.obsidian, padding: "64px clamp(16px,4vw,56px)", textAlign: "center" }}
    >
      <p style={sans({ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.20em", textTransform: "uppercase", color: PJ.gold, margin: "0 0 14px" })}>
        Ready to create yours?
      </p>
      <h2 style={serif({ fontSize: "clamp(1.8rem,3.5vw,2.8rem)", fontWeight: 400, color: PJ.ivory, margin: "0 0 14px", lineHeight: 1.12 })}>
        Plan an event like this
      </h2>
      <p style={sans({ fontSize: "0.92rem", color: PJ.slate, maxWidth: 420, margin: "0 auto 32px", lineHeight: 1.65 })}>
        Tell us what you're imagining and our planners will help shape it from the very first idea.
      </p>

      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        {/* Plan One Like This — EXP-01G live */}
        <button
          data-testid="story-plan-like-this"
          disabled={planningInspiration}
          onClick={handlePlanLikeThis}
          style={{
            ...sans({ fontSize: 14, fontWeight: 600,
                      color: planningInspiration ? PJ.slate : PJ.white }),
            background: planningInspiration ? "rgba(255,255,255,0.08)" : PJ.gold,
            border: `1.5px solid ${planningInspiration ? "rgba(255,255,255,0.14)" : PJ.gold}`,
            borderRadius: 8, padding: "12px 28px",
            cursor: planningInspiration ? "not-allowed" : "pointer",
            transition: "opacity 0.15s, background 0.15s",
          }}
          onMouseEnter={e => { if (!planningInspiration) e.currentTarget.style.opacity = "0.88"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
        >
          {planningInspiration ? "Preparing…" : "Plan One Like This"}
        </button>

        {/* Build My Event — live */}
        <button
          data-testid="story-build-my-event"
          onClick={() => nav("/build-my-event")}
          style={{
            ...sans({ fontSize: 14, fontWeight: 600, color: PJ.white }),
            background: PJ.ruby, border: "none", borderRadius: 8,
            padding: "12px 28px", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 8,
            transition: "opacity 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
        >
          Build My Event <ArrowRight size={14} />
        </button>
      </div>
    </section>
  );
}

// ── Polished 404 ───────────────────────────────────────────────────────────────
function StoryNotFound() {
  const nav = useNavigate();
  return (
    <div
      data-testid="story-not-found"
      style={{
        minHeight: "100vh", background: PJ.obsidian,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "40px 24px", textAlign: "center",
      }}
    >
      <p style={sans({ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.20em", textTransform: "uppercase", color: PJ.gold, margin: "0 0 16px" })}>
        Event not found
      </p>
      <h1 style={serif({ fontSize: "clamp(2rem,4vw,3rem)", fontWeight: 400, color: PJ.ivory, margin: "0 0 16px", lineHeight: 1.1 })}>
        This story doesn&apos;t exist yet
      </h1>
      <p style={sans({ fontSize: "0.9rem", color: PJ.slate, maxWidth: 360, lineHeight: 1.65, margin: "0 0 32px" })}>
        The event you're looking for may have been removed or the link may be incorrect.
        Browse our gallery to find inspiration.
      </p>
      <button
        data-testid="story-not-found-back"
        onClick={() => nav("/explore")}
        style={{
          ...sans({ fontSize: 14, fontWeight: 600, color: PJ.white }),
          background: PJ.ruby, border: "none", borderRadius: 8,
          padding: "12px 28px", cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 8,
        }}
      >
        <ArrowLeft size={14} /> Back to Explore
      </button>
    </div>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────────
function StoryLoading() {
  return (
    <div
      data-testid="story-loading"
      style={{
        minHeight: "60vh", background: PJ.obsidian,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          border: "2px solid rgba(255,255,255,0.08)", borderTopColor: PJ.gold,
          animation: "storySpin 0.8s linear infinite",
        }} />
        <p style={sans({ fontSize: "0.78rem", color: PJ.slate, margin: 0 })}>Loading event story…</p>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function EventStoryPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [event,                 setEvent]              = useState(null);
  const [loading,               setLoading]            = useState(true);
  const [notFound,              setNotFound]           = useState(false);
  const [showAllQA,             setShowAllQA]          = useState(false);
  const [planningInspiration,   setPlanningInspiration] = useState(false);

  // EXP-01F: Read journey context once at mount — single access point.
  const journeyContext = useMemo(() => getJourneyContext(), []);

  // EXP-01F: Compute ranked Q&A whenever the event resolves.
  const { primary: qaPrimary, secondary: qaSecondary, ctaItem: qaCtaItem } = useMemo(
    () => rankQAForContext(event?.contextual_qa, event, journeyContext),
    [event, journeyContext]
  );

  // EXP-01G: "Plan One Like This" — fetch transferable fields, set attribution, navigate to BME.
  const handlePlanLikeThis = async () => {
    if (!event || planningInspiration) return;
    setPlanningInspiration(true);
    try {
      const res = await fetch(`${API}/api/public/inspirations/${encodeURIComponent(slug)}/transferable`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const allSuggested = mapInspirationToJourneyFields(data.fields || {});
      const existing = loadJourneyFromStorage();
      const sessionId = existing?.journey_session_id || crypto.randomUUID();

      // Don't override fields already confirmed by the customer
      const fieldsToApply = {};
      Object.entries(allSuggested).forEach(([k, v]) => {
        if (existing?.[k]?.confirmed !== true) fieldsToApply[k] = v;
      });

      // Source attribution — only set if no prior session captured it
      const attribution = existing?.source_captured ? {} : {
        source_channel:     'WEBSITE',
        source_touchpoint:  'INSPIRATION_GALLERY',
        source_entry_point: 'PLAN_ONE_LIKE_THIS',
        source_captured:    true,
        source_first_seen:  new Date().toISOString(),
      };

      const base = existing || { journey_session_id: sessionId };
      const pathWithPlan = addJourneyPathEntry(base, 'PLAN_ONE_LIKE_THIS');

      saveJourneyToStorage({
        ...(existing || {}),
        journey_session_id: sessionId,
        ...attribution,
        journey_path: pathWithPlan,
        ...fieldsToApply,
        inspiration: {
          selected:            true,
          selected_match_slug: slug,
          inspiration_title:   data.inspiration_title || event.title,
          source:              'PORTFOLIO',
          selected_at:         new Date().toISOString(),
          suggested_field_keys: Object.keys(fieldsToApply),
        },
        event_match: { ...(existing?.event_match || {}), selected_match_slug: slug },
      });

      nav('/build-my-event');
    } catch (err) {
      console.error('EXP-01G EventStory: handlePlanLikeThis failed — navigating without context', err);
      nav('/build-my-event');
    } finally {
      setPlanningInspiration(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await fetch(`${API}/api/public/inspirations/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        if (r.status === 404) { setNotFound(true); return; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setEvent(await r.json());
      } catch (e) {
        if (!cancelled) {
          console.error("EventStory: fetch failed", e);
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <>
      <style>{storyStyles}</style>
      <StoryNav />
      {loading  && <StoryLoading />}
      {!loading && notFound && <StoryNotFound />}
      {!loading && !notFound && event && (
        <div data-testid="story-page" style={{ minHeight: "100vh", background: PJ.ivory }}>
          <Breadcrumb />
          <StoryHero event={event} />

          {/* ── Warm ivory editorial body ── */}
          <div style={{ background: PJ.ivory }}>
            <div style={{ maxWidth: 860, margin: "0 auto", padding: "56px clamp(16px,4vw,56px)" }}>

              {/* Editorial description */}
              <p
                data-testid="story-description"
                style={serif({ fontSize: "clamp(1.05rem,1.8vw,1.3rem)", fontWeight: 400, color: PJ.obsidian, lineHeight: 1.72, margin: "0 0 52px" })}
              >
                {event.description}
              </p>

              {/* Details grid */}
              <div
                data-testid="story-details-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: "24px 32px",
                  marginBottom: 48,
                  paddingBottom: 48,
                  borderBottom: `1px solid ${PJ.sand}`,
                }}
              >
                {GUEST_RANGE_LABELS[event.guest_range] && (
                  <DetailItem label="Guest Scale"  value={GUEST_RANGE_LABELS[event.guest_range]} />
                )}
                {VENUE_SETTING_LABELS[event.venue_setting] && (
                  <DetailItem label="Setting"      value={VENUE_SETTING_LABELS[event.venue_setting]} />
                )}
                {event.venue_name && (
                  <DetailItem label="Venue"        value={event.venue_name} />
                )}
                {event.location_city && (
                  <DetailItem label="City"         value={event.location_city} />
                )}
                {PLANNING_SCOPE_LABELS[event.planning_scope] && (
                  <DetailItem label="Planning"     value={PLANNING_SCOPE_LABELS[event.planning_scope]} />
                )}
              </div>

              {/* Functions */}
              {event.functions?.length > 0 && (
                <div style={{ marginBottom: 32 }} data-testid="story-functions">
                  <p style={sans({ fontSize: "0.62rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: PJ.slate, margin: "0 0 12px" })}>
                    Functions
                  </p>
                  <ChipList items={event.functions} />
                </div>
              )}

              {/* Services */}
              {event.services?.length > 0 && (
                <div
                  style={{ marginBottom: 56, paddingBottom: 56, borderBottom: `1px solid ${PJ.sand}` }}
                  data-testid="story-services"
                >
                  <p style={sans({ fontSize: "0.62rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: PJ.slate, margin: "0 0 12px" })}>
                    Services Delivered
                  </p>
                  <ChipList items={event.services} />
                </div>
              )}

              {/* Ask Prashasta — EXP-01F Contextual Q&A accordion */}
              {qaPrimary.length > 0 && (
                <div data-testid="story-qa-section">
                  <p style={sans({ fontSize: "0.62rem", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: PJ.gold, margin: "0 0 8px" })}>
                    Ask Prashasta
                  </p>
                  <h2 style={serif({ fontSize: "clamp(1.4rem,2.5vw,2rem)", fontWeight: 400, color: PJ.obsidian, margin: "0 0 28px", lineHeight: 1.15 })}>
                    Questions you might have
                  </h2>

                  {/* Primary items — always visible */}
                  <div style={{ borderTop: `1px solid ${PJ.sand}` }}>
                    {qaPrimary.map((qa, i) => (
                      <AccordionItem key={qa.id || i} q={qa.q} a={qa.a} defaultOpen={i === 0} />
                    ))}
                  </div>

                  {/* Secondary items — revealed on "Show more" */}
                  {showAllQA && qaSecondary.length > 0 && (
                    <div data-testid="qa-secondary-items">
                      {qaSecondary.map((qa, i) => (
                        <AccordionItem key={qa.id || `sec-${i}`} q={qa.q} a={qa.a} defaultOpen={false} />
                      ))}
                    </div>
                  )}

                  {/* Show more / collapse button */}
                  {qaSecondary.length > 0 && (
                    <button
                      data-testid="qa-show-more-btn"
                      onClick={() => setShowAllQA(v => !v)}
                      style={{
                        marginTop: 20,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        background: "none",
                        border: `1px solid ${PJ.sand}`,
                        borderRadius: 24,
                        padding: "8px 20px",
                        cursor: "pointer",
                        color: PJ.slate,
                        fontSize: "0.82rem",
                        fontFamily: "inherit",
                        transition: "border-color 0.18s, color 0.18s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = PJ.gold; e.currentTarget.style.color = PJ.obsidian; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = PJ.sand;  e.currentTarget.style.color = PJ.slate; }}
                    >
                      <ChevronDown size={13} style={{ transition: "transform 0.2s", transform: showAllQA ? "rotate(180deg)" : "none" }} />
                      {showAllQA
                        ? "Show fewer questions"
                        : `Show ${qaSecondary.length} more question${qaSecondary.length !== 1 ? "s" : ""}`}
                    </button>
                  )}

                  {/* CTA fallback — shown when primary list is thin */}
                  {qaPrimary.length < 3 && (
                    <p
                      data-testid="qa-cta-fallback"
                      style={sans({
                        marginTop: 28,
                        fontSize: "0.88rem",
                        color: PJ.slate,
                        lineHeight: 1.65,
                        borderLeft: `3px solid ${PJ.gold}`,
                        paddingLeft: 16,
                      })}
                    >
                      {qaCtaItem?.a ||
                        "Have a specific question about this event? Build My Event and tell us what you are planning."}
                    </p>
                  )}
                </div>
              )}

              {/* Fallback: no ranked Q&A at all */}
              {qaPrimary.length === 0 && !loading && (
                <div data-testid="qa-empty-fallback" style={{ marginBottom: 40 }}>
                  <p style={sans({ fontSize: "0.88rem", color: PJ.slate, lineHeight: 1.65, borderLeft: `3px solid ${PJ.gold}`, paddingLeft: 16 })}>
                    Have a specific question about this event? Build My Event and tell us what you are planning.
                  </p>
                </div>
              )}

            </div>
          </div>

          {/* Gallery — shown when event has multiple images */}
          <GalleryStrip images={event.gallery_images} title={event.title} />

          {/* CTA band */}
          <StoryCTABand planningInspiration={planningInspiration} handlePlanLikeThis={handlePlanLikeThis} />
        </div>
      )}
    </>
  );
}
